import { domElementIds } from "@shared/constants/common";
import { BEST_AVAILABLE_SLIDE_HEIGHT_PERCENT } from "@shared/modules/seating/features/BestAvailable/constants";
import { useCallback, useEffect, useState } from "react";

export interface UseSeatingHeightOptions {
  /**
   * Whether to include the best available slide height in calculations
   * @default false
   */
  includeSlide?: boolean;

  /**
   * Only calculate height on mobile viewports
   * @default false
   */
  mobileOnly?: boolean;

  /**
   * Whether the mobile slide is currently open (required if mobileOnly is true)
   */
  isSlideOpen?: boolean;

  /**
   * Whether currently on mobile viewport (required if mobileOnly is true)
   */
  isMobile?: boolean;

  /**
   * Output format for the height value
   * - "calc": Returns `calc(100vh - Xpx)` format
   * - "px": Returns `Xpx` format
   * @default "px"
   */
  format?: "calc" | "px";

  /**
   * Default value when conditions aren't met (e.g., not mobile, slide not open)
   * @default undefined (no max-height constraint)
   */
  defaultValue?: string;
}

/**
 * Custom hook for calculating seating-related container heights.
 * Accounts for navbar, banners, and optionally the best available slide.
 *
 * @example
 * // Desktop sidebar container
 * const height = useSeatingHeight({ format: "calc", defaultValue: "100%" });
 *
 * @example
 * // Mobile map with slide
 * const height = useSeatingHeight({
 *   includeSlide: true,
 *   mobileOnly: true,
 *   isSlideOpen: true,
 *   isMobile: true,
 * });
 */
export function useSeatingHeight({
  includeSlide = false,
  mobileOnly = false,
  isSlideOpen = false,
  isMobile = false,
  format = "px",
  defaultValue,
}: UseSeatingHeightOptions = {}): string | undefined {
  const [height, setHeight] = useState<string | undefined>(defaultValue);

  const { navbarId, navbarBannerId, seatingBannerId } = domElementIds;

  const calculateHeight = useCallback(() => {
    // Early return if mobileOnly is enabled but conditions aren't met
    if (mobileOnly && (!isMobile || !isSlideOpen)) {
      setHeight(defaultValue);
      return;
    }

    // Use offsetHeight for full element height (includes content, padding, borders, scrollbars)
    const navbarHeight = document?.getElementById(navbarId)?.offsetHeight ?? 0;
    const bannerHeight =
      document?.getElementById(navbarBannerId)?.offsetHeight ?? 0;
    const seatingBannerHeight =
      document?.getElementById(seatingBannerId)?.offsetHeight ?? 0;

    let totalUsedHeight = navbarHeight + bannerHeight + seatingBannerHeight;

    // Add slide height if requested
    if (includeSlide) {
      const slideHeight =
        window.innerHeight * BEST_AVAILABLE_SLIDE_HEIGHT_PERCENT;
      totalUsedHeight += slideHeight;
    }

    // Calculate available height
    const availableHeight = window.innerHeight - totalUsedHeight;

    // Format output
    if (format === "calc") {
      setHeight(`calc(var(--100dvh) - ${totalUsedHeight}px)`);
    } else {
      setHeight(`${availableHeight}px`);
    }
  }, [
    navbarId,
    navbarBannerId,
    seatingBannerId,
    includeSlide,
    mobileOnly,
    isSlideOpen,
    isMobile,
    format,
    defaultValue,
  ]);

  useEffect(() => {
    calculateHeight();

    // Recalculate on window resize
    window.addEventListener("resize", calculateHeight);
    return () => window.removeEventListener("resize", calculateHeight);
  }, [calculateHeight]);

  return height;
}
